/**
 * `plcSyncApi` — small RTK Query slice for the "Update PLC" wizard flow served
 * by `axilon-api-service` under `/api/ontology/plc-sync/*`.
 *
 * Kept separate from the big `ingestApi` slice (its own cache + tag space) but
 * sharing `ontologyBaseQuery` (API key + identity Bearer + branch/site) like
 * every other ontology slice. Wizard endpoints use absolute
 * `${ONTOLOGY_SERVICE_URL}` URLs (the api-service BFF host); PLC claim
 * read/unlink uses the absolute proxy v1 base so `ontologyBaseQuery` does not
 * re-root `/api/v1/ontology/*` below its own `/api/v1/ontology` base.
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import type {
  PlcRepoLink,
  PlcSyncEligibilityResponse,
  PlcSyncEligibleController,
  PlcSyncIneligibleController,
  PlcSyncLaunchItem,
  PlcSyncRequest
} from '@atbash-labs/proxy-client'

import { getOntologyServiceBaseUrl } from '../../config'
import { ontologyBaseQuery, PROXY_API_V1_URL } from '../core'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export type { PlcSyncRequest as PlcSyncStatus, PlcSyncState } from '@atbash-labs/proxy-client'

export type EligiblePlcController = PlcSyncEligibleController
export type IneligiblePlcController = PlcSyncIneligibleController
export type PlcSyncEligibleResponse = PlcSyncEligibilityResponse
export type LaunchPlcUpdateItem = PlcSyncLaunchItem
export type PlcRepositoryLink = PlcRepoLink

export interface LaunchPlcUpdateArgs {
  siteId: string
  branchId?: string
  groupId?: string
  items: LaunchPlcUpdateItem[]
}

export interface LaunchPlcUpdateResult {
  jobs: Array<{ jobId: string; branchId?: string; controller_name: string }>
}

export const plcSyncApi = createApi({
  reducerPath: 'plcSyncApi',
  baseQuery: ontologyBaseQuery,
  tagTypes: ['PlcRepoLinks', 'PlcSyncEligible', 'PlcSyncStatus'],
  endpoints: (builder) => ({
    getPlcRepoLinks: builder.query<PlcRepoLink[], void>({
      query: () => `${PROXY_API_V1_URL}/ontology/devops-links/plc-repo-links`,
      transformResponse: (response: { links?: PlcRepoLink[] } | PlcRepoLink[]) =>
        Array.isArray(response) ? response : (response.links ?? []),
      providesTags: ['PlcRepoLinks']
    }),
    unlinkPlcRepo: builder.mutation<void, string>({
      query: (id) => ({
        url: `${PROXY_API_V1_URL}/ontology/devops-links/plc-repo-links/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['PlcRepoLinks', 'PlcSyncEligible']
    }),
    getPlcSyncEligible: builder.query<
      PlcSyncEligibleResponse,
      { siteId: string; branchId?: string }
    >({
      query: ({ siteId, branchId }) => {
        const params = new URLSearchParams({ siteId })
        if (branchId) params.set('branchId', branchId)
        return `${ONTOLOGY_SERVICE_URL}/plc-sync/eligible?${params.toString()}`
      },
      providesTags: ['PlcSyncEligible']
    }),
    launchPlcUpdate: builder.mutation<LaunchPlcUpdateResult, LaunchPlcUpdateArgs>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/plc-sync/launch`,
        method: 'POST',
        body
      })
    }),
    getPlcSyncStatus: builder.query<PlcSyncRequest, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/plc-sync/jobs/${encodeURIComponent(jobId)}/status`,
      providesTags: (_result, _error, jobId) => [{ type: 'PlcSyncStatus', id: jobId }]
    })
  })
})

export const {
  useGetPlcRepoLinksQuery,
  useUnlinkPlcRepoMutation,
  useGetPlcSyncEligibleQuery,
  useLaunchPlcUpdateMutation,
  useGetPlcSyncStatusQuery,
  useLazyGetPlcSyncStatusQuery
} = plcSyncApi
